import { useContext, useMemo } from "react";
import { AppContext } from "../store/AppProvider";
import { ALLOWED_INDIRECT_ROLES, USERTYPES, USER_ROLES } from "../utils/constants";
import { lastLoggedInToken } from "./useManageTabSession";

function useUser() {
	const { userState } = useContext(AppContext);
	const userDetails = useMemo(() => {
		const isLoggedIn = userState?.loggedIn;
		const isImpersonated = userState?.impersonated;
		const activeUser = userState.impersonated ? userState.impersonatedUser : userState.cognitoUser;
		const activeRoles = userState?.sapUserRole?.length
			? userState.sapUserRole
			: userState.impersonated
				? userState.impersonatedRoles
				: userState.cognitoRoles;
		const activeAccount = userState.impersonated ? userState.impersonatedAccountObj : null;
		const activeContact = userState.impersonated ? userState.impersonateContactObj : null;
		const activeUserType = userState.impersonated ? "B2B" : userState?.userType;
		const isInternal = userState?.userType?.toUpperCase() === USERTYPES.INTERNAL;
		const externalAccessStatus = checkExternalAuthScenarios(userState?.sapUserRole);
		const isAuthorized = isInternal || externalAccessStatus.allowed ? true : false;
		const activeCustomerGroup = userState.impersonated ? userState?.impersonatedCustomerGrp : userState?.customerGroup;
		const activeSalesOrg = userState.impersonated ? userState?.impersonatedSalesOrg : userState?.salesOrg;
		const activeAMContact = userState.impersonated ? userState?.impersonatedaAMDetails : userState?.amDetails;
		const activeUserEmail = userState.impersonated ? userState?.impersonatedUser?.email : userState?.cognitoUser?.email;
		const cognitoRoles = userState.cognitoRoles;
		const externalDirectUserRoles = userState?.sapUserRole ?? [];
		const isSalesNewsAdmin = !!userState?.isSalesNewsAdmin;
		const hasSalesNewsAccess = !!userState?.hasSalesNewsAccess;

		// Centralized DB-driven permissions object
		const dbPermissions = {
			salesNewsVgsap: { hasAccess: hasSalesNewsAccess, isAdmin: isSalesNewsAdmin },
			// Future modules can be added here:
			// anotherModule: { hasAccess: !!userState?.hasAnotherModuleAccess, isAdmin: false },
		};

		return {
			isAuthorized,
			isInternal,
			isLoggedIn,
			isImpersonated,
			activeUser,
			activeRoles,
			activeAccount,
			activeContact,
			activeUserType,
			activeCustomerGroup,
			activeSalesOrg,
			activeAMContact,
			activeUserEmail,
			cognitoRoles,
			externalAccessStatus,
			externalDirectUserRoles,
			isAccountRep: !!userState?.isAccountRep,
			isSuperAdmin: !!userState?.isSuperAdmin,
			isSalesNewsAdmin,
			hasSalesNewsAccess,
			dbPermissions,
		};
	}, [userState]);

	return userDetails;
}

// Helper: Decode token safely
function getTokenPayload() {
	const token = lastLoggedInToken();
	if (!token) return null;

	try {
		return JSON.parse(atob(token.split(".")[1]));
	} catch {
		return null;
	}
}

// Helper: Extract common auth data
function extractAuthData(payload) {
	if (!payload) return {};

	const sapAccount = payload["custom:sapAccountid"]?.trim();
	const sapContact = payload["custom:sapcontactid"]?.trim();
	const accessRoles = payload["custom:accessrole"]?.trim()?.split(",") ?? [];
	const gsapRoles = [USER_ROLES.ACCMGR, USER_ROLES.ACTREP,
	USER_ROLES.DS_ACTREP, USER_ROLES.REGMGR];

	const hasInDirectAccess = accessRoles.some(role =>
		ALLOWED_INDIRECT_ROLES.includes(role)
	);

	const hasGSPAccess = accessRoles.some(role =>
		gsapRoles.includes(role)
	);

	return {
		sapAccount,
		sapContact,
		accessRoles,
		hasInDirectAccess,
		hasGSPAccess,
	};
}

// External Auth Check
function checkExternalAuthScenarios(sapRoles) {
	const payload = getTokenPayload();
	if (!payload) {
		return { allowed: false, reason: "Not Logged in" };
	}

	const { sapAccount, sapContact, hasInDirectAccess, hasGSPAccess } = extractAuthData(payload);

	if (!sapAccount && !sapContact && !hasInDirectAccess && !hasGSPAccess) {
		return { allowed: false, reason: "No Access" };
	}

	if ((sapAccount && !sapContact) || (!sapAccount && sapContact)) {
		return { allowed: false, reason: "MisConfigured Account" };
	}

	if (sapAccount && sapContact && !sapRoles?.length) {
		return { allowed: false, reason: "Missing GSAP" };
	}

	if (sapRoles?.length && (hasInDirectAccess || hasGSPAccess)) {
		return { allowed: false, reason: "Bad Account" };
	}

	return { allowed: true };
}

export default useUser;
